<template>
  <div :style="{ width: width }">
    <div ref="myEchart" :class="className" :style="{ height: height, width: '100%' }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
require('echarts/map/js/province/shandong'); // 引入山东地图数据

export default {
  name: 'ShandongMap',
  props: {
    // CSS 类名
    className: {
      type: String,
      default: 'chart'
    },
    // 宽度
    width: {
      type: String,
      default: '100%'
    },
    // 高度
    height: {
      type: String,
      default: '500px'
    },
    // 是否自动响应窗口 resize
    autoResize: {
      type: Boolean,
      default: true
    },
    // 地图数据，格式：[{ name: '济南市', value: 120 }, ...]
    mapData: {
      type: Array,
      required: true,
      validator(value) {
        // 可选：校验数据格式
        return value.every(item => typeof item.name === 'string' && typeof item.value !== 'undefined');
      }
    },
    // visualMap 分级配置（可完全自定义）
    visualMapPieces: {
      type: Array,
      default() {
        return [
          { gt: 100, label: '> 100 次', color: '#3c82e1' },
          { gte: 50, lte: 100, label: '50 - 100次', color: '#27e1e6' },
          { gte: 20, lt: 50, label: '10 - 50 次', color: '#e1783c' },
          { gt: 0, lt: 20, label: '1 - 10 次', color: '#e6e622' },
          { value: 0, label: '无扫码', color: '#3ce15a' }
        ];
      }
    },
    // 是否显示 visualMap 图例
    showVisualMap: {
      type: Boolean,
      default: true
    },
    // 地图标题
    titleText: {
      type: String,
      default: '山东省产品扫码热度图'
    },
    tooltipName:{
      type: String,
      default: '扫码次数'
    },
    tooltipUnit:{
      type: String,
      default: '次'
    }
  },
  data() {
    return {
      chart: null, // ECharts 实例
      mapname: '山东' // 地图名称，对应 echarts.registered map name
    };
  },
  watch: {
    // 监听数据变化，更新图表
    mapData: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
      deep: true // 深度监听数组内容变化
    },
    visualMapPieces: {
      handler() {
        this.$nextTick(() => {
          this.updateChart();
        });
      },
      deep: true
    },
    showVisualMap() {
      this.$nextTick(() => {
        this.updateChart();
      });
    },
    titleText() {
      this.$nextTick(() => {
        this.updateChart();
      });
    }
  },
  mounted() {
    this.initChart();
    this.setupResizeListener();
  },
  methods: {
    // 初始化 ECharts 实例
    initChart() {
      this.chart = echarts.init(this.$refs.myEchart);
      this.updateChart(); // 初始渲染
    },

    // 更新图表的核心方法
    updateChart() {
      // 防空判断
      if (!this.chart) return;
      if (!this.mapData || this.mapData.length === 0) {
        console.warn('[ShandongMap] mapData 为空，跳过渲染');
        return;
      }

      // 构建 ECharts 配置项
      const option = {
        backgroundColor: 'transparent',
        title: {
          show: true,
          text: this.titleText,
          left: 'center',
          textStyle: {
            fontSize: 16,
            fontWeight: 'normal',
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            return `${params.name}<br/>${this.tooltipName}: ${params.value || 0} ${this.tooltipUnit}`;
          }
        },
        series: [
          {
            name: this.tooltipName,
            type: 'map',
            map: this.mapname,
            roam: false, // 禁止缩放和平移
            zoom: 1.2,
            label: {
              normal: {
                show: true,
                fontSize: 13,
                color: '#333'
              },
              emphasis: {
                show: false // 鼠标悬停时不显示 label
              }
            },
            itemStyle: {
              normal: {
                // ✅ 重要：不要设置 areaColor！
                // 让 visualMap 完全控制颜色
                borderColor: '#99d0ec',
                borderWidth: 0.8
              },
              emphasis: {
                areaColor: '#fe9b7d' // 悬停时高亮色
              }
            },
            data: this.mapData // 父组件传入的完整数据（建议已补全）
          }
        ]
      };

      // 只有 showVisualMap 为 true 时才添加 visualMap 组件
      if (this.showVisualMap) {
        option.visualMap = {
          type: 'piecewise', // 分段型 visualMap
          pieces: this.visualMapPieces,
          right: 26,
          bottom: 40,
          showLabel: true,
          text: ['高', '低'], // 显示文本
          show: true,
          orient: 'vertical',
          itemWidth: 14,
          itemHeight: 14,
          textStyle: {
            fontSize: 12,
            color: '#666'
          },
          dimension: 0, // 映射到 series.data 的 value 字段
          inRange: {
            color: this.visualMapPieces.map(p => p.color) // 可选：显式指定颜色范围
          }
        };
      }

      // ✅ 关键：使用 notMerge = false
      // 让 ECharts 合并配置，避免状态丢失
      this.chart.setOption(option, false);
    },

    // 处理窗口 resize
    resizeChart() {
      if (this.chart) {
        this.chart.resize();
      }
    },

    // 绑定 resize 监听
    setupResizeListener() {
      if (this.autoResize) {
        window.addEventListener('resize', this.resizeChart);
      }
    }
  },
  beforeDestroy() {
    // 组件销毁前清理资源
    if (this.chart) {
      window.removeEventListener('resize', this.resizeChart);
      this.chart.dispose(); // 销毁实例
      this.chart = null;
    }
  }
};
</script>

<style scoped>
.chart {
  width: 100%;
}
</style>
